import config from './config'
import { isSisterStore } from './sisterStore'

const KEY = 'madamy-story-cart-v1'
export const CART_CHANGED = 'madamy-story-cart'

function asPrice(value) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

function clean(items) {
  if (!Array.isArray(items)) return []
  return items
    .map((item) => ({
      id: String(item?.id || ''),
      url: item?.url || '',
      title: item?.title || 'Peça',
      brand: item?.brand || '',
      price: asPrice(item?.price),
      qty: Math.max(1, Math.min(9, Math.floor(Number(item?.qty) || 1))),
    }))
    .filter((item) => item.id)
}

function looksRawName(text) {
  const s = String(text || '')
  return /T\d{2}:\d{2}/.test(s)
    || /\d{4}-\d{2}-\d{2}/.test(s)
    || /\bP\d{2,}\b/i.test(s)
    || /\bI\d{2,}\b/i.test(s)
    || /_\d{2,}/.test(s)
}

const KIND_RULES = [
  [/oculos|óculos|sunglass/i, 'Óculos'],
  [/bolsa|neverfull|speedy|keepall|tote|\bbag\b|flap/i, 'Bolsa'],
  [/cinto|\bbelt\b/i, 'Cinto'],
  [/carteira|wallet|cardholder/i, 'Carteira'],
  [/relogio|relógio|\bwatch\b/i, 'Relógio'],
  [/sapato|scarpin|sneaker|sandalia|tênis|tenis|mule|bota/i, 'Calçado'],
  [/lenco|lenço|scarf/i, 'Lenço'],
]

const BRAND_NAMES = [
  'Louis Vuitton', 'Saint Laurent', 'Bottega Veneta', 'Dolce Gabbana',
  'Balenciaga', 'Valentino', 'Burberry', 'Givenchy', 'Miu Miu',
  'Chanel', 'Gucci', 'Dior', 'Prada', 'Hermes', 'Fendi', 'Celine',
  'Loewe', 'Goyard', 'Chloe', 'Versace',
]

function detectKind(blob) {
  const hit = KIND_RULES.find(([re]) => re.test(blob))
  return hit ? hit[1] : ''
}

export function detectKindFromUrl(url) {
  return detectKind((url || '').split('/').pop().replace(/[_-]/g, ' '))
}

function detectBrand(blob) {
  const folded = String(blob || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  if (/(^|[^a-z])lv([^a-z]|$)|louis\s*vuitton|vuitton/.test(folded)) return 'Louis Vuitton'
  if (/(^|[^a-z])ysl([^a-z]|$)|saint\s*laurent/.test(folded)) return 'Saint Laurent'
  return BRAND_NAMES.find((brand) => {
    const key = brand.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    return folded.includes(key) || folded.includes(key.replace(/\s+/g, ''))
  }) || ''
}

function stripCodes(text) {
  return String(text || '')
    .replace(/\d{4}-\d{2}-\d{2}T[^\s]+/g, ' ')
    .replace(/\d{2}T\d{2}:\d{2}:\d{2}[^\s]*/g, ' ')
    .replace(/[+-]\d{2}:\d{2}/g, ' ')
    .replace(/\b\d{2}:\d{2}(:\d{2})?\b/g, ' ')
    .replace(/\bP\d+[A-Z]?\d*\b/gi, ' ')
    .replace(/\bI\d+\b/gi, ' ')
    .replace(/\b20[2-3]\d\b/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

const KIND_STRIP = /oculos|óculos|sunglasses?|bolsa|neverfull|speedy|keepall|tote|\bbag\b|flap|cinto|\bbelt\b|carteira|wallet|cardholder|relogio|relógio|\bwatch\b|sapato|scarpin|sneaker|sandalia|tênis|tenis|mule|bota|lenco|lenço|scarf/gi

function kindSuffix(text) {
  const words = stripCodes(text)
    .replace(KIND_STRIP, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => /^\d{1,3}$/.test(w) || (/^[A-Za-zÀ-ÿ]{3,12}$/.test(w) && !looksRawName(w)))
  return words.slice(0, 2).join(' ')
}

export function titleFromImageUrl(url) {
  if (!url) return ''
  try {
    const name = decodeURIComponent(String(url).split('?')[0].split('/').pop() || '')
      .replace(/\.(jpe?g|png|webp)$/i, '')
    if (!name || /^[bv][_\s-]?\d+$/i.test(name) || /^\d+$/.test(name)) return ''
    return name
  } catch {
    return ''
  }
}

const KIND_I18N = {
  pt: { oculos: 'Óculos', bolsa: 'Bolsa', cinto: 'Cinto', carteira: 'Carteira', relogio: 'Relógio', sapato: 'Calçado', lenco: 'Lenço', peca: 'Peça' },
  en: { oculos: 'Sunglasses', bolsa: 'Bag', cinto: 'Belt', carteira: 'Wallet', relogio: 'Watch', sapato: 'Shoes', lenco: 'Scarf', peca: 'Piece' },
  es: { oculos: 'Lentes', bolsa: 'Cartera', cinto: 'Cinturón', carteira: 'Billetera', relogio: 'Reloj', sapato: 'Zapato', lenco: 'Pañuelo', peca: 'Pieza' },
  ru: { oculos: 'Очки', bolsa: 'Сумка', cinto: 'Ремень', carteira: 'Кошелёк', relogio: 'Часы', sapato: 'Обувь', lenco: 'Платок', peca: 'Вещь' },
}

const KIND_KEYS = [
  [/oculos|óculos|sunglass/i, 'oculos'],
  [/bolsa|neverfull|speedy|keepall|tote|\bbag\b|flap/i, 'bolsa'],
  [/cinto|\bbelt\b/i, 'cinto'],
  [/carteira|wallet|cardholder/i, 'carteira'],
  [/relogio|relógio|\bwatch\b/i, 'relogio'],
  [/sapato|scarpin|sneaker|sandalia|tênis|tenis|mule|bota/i, 'sapato'],
  [/lenco|lenço|scarf/i, 'lenco'],
]

export function brandFromUrl(url) {
  const name = titleFromImageUrl(url)
  return detectBrand([name, url].filter(Boolean).join(' '))
}

export function prettyStoryTitle(url, locale = 'pt') {
  const name = titleFromImageUrl(url)
  const blob = [name, url].filter(Boolean).join(' ')
  const kindKey = (KIND_KEYS.find(([re]) => re.test(blob)) || [])[1] || 'peca'
  const brand = detectBrand(blob)
  const kinds = KIND_I18N[locale] || KIND_I18N.en
  const kind = kinds[kindKey] || kinds.peca
  if (brand) return `${kind} ${brand}`
  return kind
}

export function prettyCartLabel(item) {
  const title = String(item?.title || '').trim()
  const brand = String(item?.brand || '').trim()
  const fromUrl = titleFromImageUrl(item?.url || '')
  const blob = [brand, title, fromUrl, item?.url].filter(Boolean).join(' ')
  const kind = detectKind(blob)
  const brandName = detectBrand(blob) || (!looksRawName(brand) && brand.length < 24 ? brand : '')

  if (kind && brandName) return `${kind} ${brandName}`
  if (kind) {
    const extra = kindSuffix(title || fromUrl)
    return extra ? `${kind} ${extra}` : kind
  }
  if (brandName) return brandName

  if (title && !looksRawName(title) && title.length < 36) {
    return title.replace(/oculos/gi, 'Óculos')
  }

  const cleaned = stripCodes(title || fromUrl)
    .replace(/oculos/gi, 'Óculos')
    .split(' ')
    .filter((w) => w.length > 1 && !/^\d+$/.test(w))
    .slice(0, 3)
    .join(' ')
  return cleaned || 'Peça'
}

export function readStoryCart() {
  if (typeof window === 'undefined') return []
  try {
    return clean(JSON.parse(window.localStorage.getItem(KEY) || '[]'))
  } catch {
    return []
  }
}

export function writeStoryCart(items) {
  if (typeof window === 'undefined') return
  const next = clean(items)
  window.localStorage.setItem(KEY, JSON.stringify(next))
  window.dispatchEvent(new Event(CART_CHANGED))
}

export function storyCartCount(items) {
  return (items || []).reduce((sum, item) => sum + (item.qty || 0), 0)
}

export function storyCartTotal(items) {
  return (items || []).reduce((sum, item) => {
    if (item.price == null) return sum
    return sum + item.price * (item.qty || 0)
  }, 0)
}

export function storyCartUnpriced(items) {
  return (items || []).reduce((sum, item) => (item.price == null ? sum + (item.qty || 0) : sum), 0)
}

export function cartCurrency() {
  if (!isSisterStore()) return 'BRL'
  return String(config.STORE_CURRENCY || 'USD').toUpperCase()
}

export function fmtMoney(value, currency = cartCurrency()) {
  if (value == null) return currency === 'BRL' ? 'preço na foto' : 'price on photo'
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  if (currency === 'USD') {
    return `US$ ${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  if (currency === 'EUR') {
    return `€ ${n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
  if (currency === 'RUB') {
    return `₽ ${n.toLocaleString('ru-RU', { maximumFractionDigits: 0 })}`
  }
  return `R$ ${n.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}`
}

export function fmtBrl(value) {
  return fmtMoney(value)
}

export function hydrateStoryCartPrices(prices) {
  if (!prices || typeof prices !== 'object') return readStoryCart()
  let changed = false
  const items = readStoryCart().map((item) => {
    if (item.price != null) return item
    const key = String(item.url || '').split('?')[0]
    const next = asPrice(prices[item.url] ?? prices[key] ?? prices[item.id])
    if (next == null) return item
    changed = true
    return { ...item, price: next }
  })
  if (changed) writeStoryCart(items)
  return items
}

export function addStoryCartItem(piece) {
  if (!piece?.id) return readStoryCart()
  const incoming = {
    id: String(piece.id),
    url: piece.url || '',
    title: prettyCartLabel({ title: piece.title, brand: piece.brand, url: piece.url }),
    brand: piece.brand || '',
    price: asPrice(piece.price),
    qty: 1,
  }
  const items = readStoryCart()
  const idx = items.findIndex((item) => item.id === incoming.id)
  if (idx >= 0) {
    const prev = items[idx]
    items[idx] = {
      ...prev,
      ...incoming,
      qty: Math.min(9, prev.qty + 1),
      price: incoming.price ?? prev.price,
      url: incoming.url || prev.url,
    }
  } else {
    items.push(incoming)
  }
  writeStoryCart(items)

  // Grava no banco na hora (radar Carrinhos + histórico). Não espera o batch do tracker.
  import('./apiInstance').then(({ default: api }) => {
    api.post('/cart/signal', {
      kind: 'add',
      page: typeof window !== 'undefined' ? window.location.pathname : '/cart',
      item: {
        id: incoming.id,
        url: incoming.url,
        brand: incoming.brand,
        category: detectKind(incoming.url + ' ' + incoming.title),
        title: incoming.title,
        price: incoming.price,
      },
    }).catch(() => {})
  }).catch(() => {})

  import('./trackerBus').then(({ trackPiece }) => {
    trackPiece('add_to_cart', {
      page: typeof window !== 'undefined' ? window.location.pathname : '/cart',
      button: 'add_cart',
      id: incoming.id,
      url: incoming.url,
      image_url: incoming.url,
      brand: incoming.brand,
      title: incoming.title,
      price: incoming.price,
    })
  }).catch(() => {})
  return items
}

export function bumpStoryCartItem(id, delta) {
  const items = readStoryCart()
    .map((item) =>
      item.id === String(id) ? { ...item, qty: item.qty + delta } : item,
    )
    .filter((item) => item.qty > 0)
  writeStoryCart(items)
  return items
}
